import type { InviteMemberInput, InviteMemberResponse, OrgMemberRole } from "@solomon-apps/shared";
import { useEffect, useMemo, useRef, useState } from "react";

import { asTeamError, inviteEmailProblem } from "./teamData";

/**
 * Create Invitation's outcome states. `created` holds while the page leaves
 * for the new member; `failed` holds briefly, then settles to `retry`.
 */
export type CreatePhase = "idle" | "creating" | "created" | "failed" | "retry";

export const CREATE_LABELS: Record<CreatePhase, string> = {
    idle: "Send Invite",
    creating: "Sending...",
    created: "Invite Sent",
    failed: "Not Sent",
    retry: "Retry"
};

/** How long "Created" holds before the page moves on (ms). */
export const CREATED_HOLD_MS = 800;
/** How long "Not Created" holds before settling to Retry (ms). */
export const FAILED_HOLD_MS = 2000;

export interface UseAddMemberReturn {
    fullName: string;
    setFullName: (next: string) => void;
    email: string;
    setEmail: (next: string) => void;
    role: OrgMemberRole;
    setRole: (next: OrgMemberRole) => void;
    /** Why the address cannot go yet, or null. */
    emailProblem: string | null;
    /** The email field has been left (or a submit tried): show it invalid. */
    emailTouched: boolean;
    touchEmail: () => void;
    phase: CreatePhase;
    /** The last failure, worded for the console; null when none. */
    failure: string | null;
    submit: () => Promise<void>;
}

/**
 * The Add Member page's form: one address, an optional name, a role
 * (Member by default). Submitting sends one invite; on success, after the
 * "Created" hold, `onCreated` gets the response (the page navigates to the
 * new member). A failure's reason is kept for the button, on demand.
 */
export function useAddMember({
    existingEmails,
    invite,
    onCreated
}: {
    existingEmails: string[];
    invite: (input: InviteMemberInput) => Promise<InviteMemberResponse>;
    onCreated: (response: InviteMemberResponse) => void;
}): UseAddMemberReturn {
    const [fullName, setFullNameState] = useState("");
    const [email, setEmailState] = useState("");
    const [role, setRoleState] = useState<OrgMemberRole>("Member");
    const [emailTouched, setEmailTouched] = useState(false);
    const [phase, setPhase] = useState<CreatePhase>("idle");
    const [failure, setFailure] = useState<string | null>(null);
    const holdTimer = useRef<number | null>(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return (): void => {
            mounted.current = false;
            if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
        };
    }, []);

    const emailProblem = useMemo(() => inviteEmailProblem(email, existingEmails), [email, existingEmails]);

    const hold = (ms: number, then: () => void): void => {
        if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
        holdTimer.current = window.setTimeout(() => {
            holdTimer.current = null;
            then();
        }, ms);
    };

    // An edit after a failure starts a fresh attempt: the old reason no
    // longer describes what is in the form.
    const edited = (): void => {
        if (phase !== "failed" && phase !== "retry") return;
        if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
        setPhase("idle");
        setFailure(null);
    };

    const submit = async (): Promise<void> => {
        if (phase === "creating" || phase === "created") return;
        setEmailTouched(true);
        if (emailProblem !== null) return;
        if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
        setPhase("creating");
        setFailure(null);
        try {
            const response = await invite({
                email: email.trim(),
                fullName: fullName.trim() || undefined,
                orgRole: role
            });
            // Cancel may have left the page while the request was pending.
            if (!mounted.current) return;
            setPhase("created");
            hold(CREATED_HOLD_MS, () => onCreated(response));
        } catch (caught) {
            if (!mounted.current) return;
            setFailure(asTeamError(caught).message);
            setPhase("failed");
            hold(FAILED_HOLD_MS, () => setPhase("retry"));
        }
    };

    return {
        fullName,
        setFullName: (next): void => {
            setFullNameState(next);
            edited();
        },
        email,
        setEmail: (next): void => {
            setEmailState(next);
            edited();
        },
        role,
        setRole: (next): void => {
            setRoleState(next);
            edited();
        },
        emailProblem,
        emailTouched,
        touchEmail: () => setEmailTouched(true),
        phase,
        failure,
        submit
    };
}
